import { isValidYmd } from '@mm/shared';

/** Sheet columns the import can use. */
export type Field = 'date' | 'spend' | 'gst' | 'revenue' | 'revenueInr';
export type Mapping = Record<Field, string>;
export type DateFormat = 'DMY' | 'MDY';

export interface MappedRow {
  line: number;
  date: string;
  spend?: string;
  gst?: string;
  revenue?: string;
  revenueInr?: string;
}

/**
 * Header guesses. They cover both sheet layouts in use:
 *  - Date | Cost | GST | Total Cost | Revenue($) | Revenue (INR) | …
 *  - Date | Cost | GST (18%) | Cost+GST | Revenue($) | Final Revenue (INR) | …
 *    (with a "Dollar Rate" line and a totals line above the data)
 */
export const FIELDS: { key: Field; label: string; help: string; guess: RegExp; avoid?: RegExp }[] =
  [
    { key: 'date', label: 'Date', help: 'Required', guess: /^date$/i },
    {
      key: 'spend',
      label: 'Ads spend before GST',
      help: 'e.g. “Cost”',
      guess: /^(cost|spend|ad ?spend|marketing)/i,
      avoid: /total|with gst|\+\s*gst|incl/i,
    },
    {
      key: 'gst',
      label: 'GST on spend',
      help: 'Optional; otherwise calculated',
      guess: /^gst$|^gst\b/i,
      avoid: /with|without/i,
    },
    {
      key: 'revenue',
      label: 'Revenue (account currency)',
      help: 'e.g. “Revenue($)”',
      guess: /revenue.*(\$|usd)|^\$? ?revenue$/i,
      avoid: /inr|₹/i,
    },
    {
      key: 'revenueInr',
      label: 'Revenue in INR',
      help: 'Optional; gives the exchange rate',
      guess: /revenue.*(inr|₹)/i,
    },
  ];

export const EMPTY_MAPPING: Mapping = { date: '', spend: '', gst: '', revenue: '', revenueInr: '' };

/** Column index (as string) for each field whose header matches; '' when none does. */
export function guessMapping(headers: string[]): Mapping {
  const next = { ...EMPTY_MAPPING };
  for (const f of FIELDS) {
    const i = headers.findIndex((h) => f.guess.test(h.trim()) && !(f.avoid?.test(h) ?? false));
    if (i >= 0) next[f.key] = String(i);
  }
  return next;
}

export function parseDate(raw: string, format: DateFormat): string | null {
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return isValidYmd(s) ? s : null;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  if (!m) return null;
  const [a, b, y] = [
    Number(m[1]),
    Number(m[2]),
    m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]),
  ];
  const [d, mo] = format === 'DMY' ? [a, b] : [b, a];
  const ymd = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return isValidYmd(ymd) ? ymd : null;
}

/** "₹ 7,969.58" → "7969.58"; empty → undefined; anything else → null (invalid). */
export function parseAmount(raw: string | undefined): string | undefined | null {
  const s = (raw ?? '').replace(/[₹$,\s]/g, '').replace(/^INR|^USD/i, '');
  if (s === '' || s === '-') return undefined;
  if (!/^\d{1,14}(\.\d+)?$/.test(s)) return null;
  const [i, f = ''] = s.split('.');
  return f.length > 4 ? `${i}.${f.slice(0, 4)}` : s;
}

/**
 * Rows to import from a sheet. Lines without a readable date (totals, notes,
 * the "Dollar Rate" line) are ignored, and so are dated lines with neither
 * spend nor revenue (empty days).
 */
export function mapRows(rows: string[][], headerRow: number, mapping: Mapping, format: DateFormat) {
  if (mapping.date === '') return { rows: [] as MappedRow[], ignored: 0, invalid: [] as string[] };
  const col = (r: string[], f: Field) => (mapping[f] === '' ? undefined : r[Number(mapping[f])]);
  const out: MappedRow[] = [];
  const invalid: string[] = [];
  let ignored = 0;
  rows.slice(headerRow + 1).forEach((r, i) => {
    const line = headerRow + i + 2;
    const date = parseDate(col(r, 'date') ?? '', format);
    if (!date) {
      if (r.some((c) => c.trim())) ignored++;
      return;
    }
    const row: MappedRow = { line, date };
    for (const f of ['spend', 'gst', 'revenue', 'revenueInr'] as const) {
      const v = parseAmount(col(r, f));
      if (v === null)
        invalid.push(
          `Line ${line}: “${col(r, f)}” is not a valid ${FIELDS.find((x) => x.key === f)!.label.toLowerCase()}`,
        );
      else if (v !== undefined) row[f] = v;
    }
    if (row.spend !== undefined || row.revenue !== undefined) out.push(row);
  });
  return { rows: out, ignored, invalid };
}
